import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { useSupplier } from '@/api/rpc/hooks/accounts-payable/useSupplier';

import { BuildingsIcon, ArrowSquareOutIcon } from '@phosphor-icons/react';

interface SupplierPreviewProps {
  entityType: SystemEntity;
  entityId: string;
  className?: string;
}

export function SupplierPreview({ entityId, className }: SupplierPreviewProps) {
  const { data: supplier, isLoading, error } = useSupplier(entityId);

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <BuildingsIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Supplier</h4>
        </div>
        <p className="text-xs text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <BuildingsIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Supplier</h4>
        </div>
        <p className="text-xs text-muted-foreground">
          Unable to load supplier details
        </p>
      </div>
    );
  }

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-3">
        <BuildingsIcon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">{supplier.name}</h4>
      </div>

      <div className="space-y-2 text-xs">
        {supplier.contactName && (
          <div>
            <span className="text-muted-foreground">Contact: </span>
            <span className="font-medium">{supplier.contactName}</span>
          </div>
        )}

        {supplier.email && (
          <div>
            <span className="text-muted-foreground">Email: </span>
            <span className="font-medium">{supplier.email}</span>
          </div>
        )}

        {supplier.phone && (
          <div>
            <span className="text-muted-foreground">Phone: </span>
            <span className="font-medium">{supplier.phone}</span>
          </div>
        )}

        {supplier.address && (
          <div>
            <span className="text-muted-foreground">Address: </span>
            <span className="font-medium">
              {[
                supplier.address.street,
                supplier.address.city,
                supplier.address.state,
                supplier.address.postalCode,
              ]
                .filter(Boolean)
                .join(', ')}
            </span>
          </div>
        )}
      </div>

      {supplier.companyId && (
        <div className="mt-3">
          <a
            href={`/companies/${supplier.companyId}?tab=supplier-info`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-full px-3 py-1.5 text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
          >
            <ArrowSquareOutIcon className="size-3 mr-1.5" />
            View
          </a>
        </div>
      )}
    </div>
  );
}
